import { Link } from 'react-router-dom';
import styled, { css, keyframes } from 'styled-components';
import { Tag, Typography } from '~/ui/components';
import { themeColor } from '~/common/utils/theme';

const pulse = keyframes`
  0% {
    box-shadow: 0 0 0 0 rgba(0, 150, 255, 0.4);
  }
  70% {
    box-shadow: 0 0 0 10px rgba(0, 150, 255, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(0, 150, 255, 0);
  }
`;

export const TicketCard = styled.div`
  background-color: ${themeColor('background.primary')};
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.12);
  padding: 12px;
  width: 100%;
  cursor: pointer;
  transition: transform 0.2s, box-shadow 0.2s;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 3px 6px rgba(0, 0, 0, 0.15);
  }

  ${({ isNew }) =>
    isNew &&
    css`
      animation: ${pulse} 2s infinite;
    `}
`;

export const TicketHeader = styled.div`
  display: flex;
  margin-bottom: 12px;
  align-items: center;
  gap: 4px;
  position: relative;
`;

export const NewBadge = styled(Tag).attrs({
  textColor: 'info.primary',
  bgColor: 'info.secondary',
  small: true,
})`
  position: absolute;
  right: 0;
  top: 64px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  z-index: 1;
`;

export const IdInfo = styled(Typography).attrs(({ color }) => ({
  type: 'bodySmall',
  weight: '600',
  element: 'div',
  color: color || 'brand.primary',
}))`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
`;

export const TicketContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

export const OpenTag = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  border: 1.5px solid ${({ theme }) => theme.colors.brand.primary};
  color: ${({ theme }) => theme.colors.brand.primary};
  border-radius: 10px;
  padding-block: 2px;
  width: fit-content;
  padding-inline: 10px;
  font-size: 12px;
  line-height: 16px;

  svg {
    width: 16px;
    height: 16px;
  }
`;

export const ResponsibleInfo = styled(Typography).attrs({
  type: 'bodySmall',
  weight: '600',
  element: 'div',
  color: 'element.secondary',
})`
  display: flex;
  justify-content: space-between;
  gap: 8px;

  span {
    font-size: 12px;
    color: ${({ theme }) => theme.colors.element.primary};
    font-weight: 400;
  }
`;

type TicketFieldProps = {
  align?: string;
};

export const TicketField = styled.div<TicketFieldProps>`
  display: flex;
  flex-direction: column;
  gap: 4px;
  align-items: ${({ align }) => align || 'flex-start'};
  font-size: 12px;
  color: ${({ theme }) => theme.colors.element.placeholder};

  strong {
    color: ${({ theme }) => theme.colors.element.secondary};
    font-weight: 700;
  }
`;

export const LinkField = styled(Link)`
  font-size: 14px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.brand.primary};
`;

export const SlaInfo = styled(Typography).attrs({
  type: 'bodyXSmall',
  weight: '400',
  element: 'div',
  color: 'element.placeholder',
})`
  display: flex;
  align-items: baseline;
  gap: 8px;

  b {
    color: ${({ theme }) => theme.colors.element.secondary};
    font-weight: 600;
  }
`;
